import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { findProduct, findPlan } from "@/lib/products";
import { devError } from "@/lib/logger";

const CartContext = createContext(null);

const STORAGE_KEY = "norton.cart.v1";

const loadInitial = () => {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    devError("Failed to load cart from storage:", error);
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadInitial);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      devError("Failed to persist cart to storage:", error);
    }
  }, [items]);

  const addItem = useCallback((productId, planId, quantity = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.product_id === productId && i.plan_id === planId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [...prev, { product_id: productId, plan_id: planId, quantity }];
    });
    setDrawerOpen(true);
  }, []);

  const removeItem = useCallback((productId, planId) => {
    setItems((prev) => prev.filter((i) => !(i.product_id === productId && i.plan_id === planId)));
  }, []);

  const updateQty = useCallback((productId, planId, quantity) => {
    if (quantity <= 0) {
      removeItem(productId, planId);
      return;
    }
    setItems((prev) =>
      prev.map((i) =>
        i.product_id === productId && i.plan_id === planId ? { ...i, quantity } : i,
      ),
    );
  }, [removeItem]);

  const clear = useCallback(() => setItems([]), []);

  const enriched = useMemo(
    () =>
      items
        .map((i) => {
          const product = findProduct(i.product_id);
          const plan = findPlan(i.product_id, i.plan_id);
          if (!product || !plan) return null;
          return {
            ...i,
            product,
            plan,
            line_total: +(plan.price * i.quantity).toFixed(2),
          };
        })
        .filter(Boolean),
    [items],
  );

  const subtotal = useMemo(
    () => +enriched.reduce((s, i) => s + i.line_total, 0).toFixed(2),
    [enriched],
  );
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  // Memoize the context value so consumers don't re-render on every CartProvider render
  const value = useMemo(
    () => ({
      items: enriched,
      rawItems: items,
      subtotal,
      count,
      addItem,
      removeItem,
      updateQty,
      clear,
      drawerOpen,
      setDrawerOpen,
    }),
    [enriched, items, subtotal, count, addItem, removeItem, updateQty, clear, drawerOpen],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
